import React, { useEffect, useState } from 'react';
import {
    Button, CircularProgress,
    Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Divider, FormHelperText,
    Grid, TextField, Typography
} from '@mui/material';
import ChipList from '@dyl/components/ChipList';
import useChangeEvent from "@dyl/hooks/useChangeEvent";
import checkTargets from "@dyl/apiClients/backendApi/checkTargets";
import SpecialSymbolStrip from "@dyl/utils/specialSymbolStrip";


const T = (t) => t === '' ? [] : t.split(',').map(x => SpecialSymbolStrip(x).trim());

export default function CopyFromExcelModal({ onAdd }) {
    const [primaryTargets, setPrimaryTargets] = useState('');
    const onPrimaryChange = useChangeEvent(setPrimaryTargets);
    const [secondaryTargets, setSecondaryTargets] = useState('');
    const onSecondaryChange = useChangeEvent(setSecondaryTargets);
    const [isOpen, setOpen] = useState(false);
    useEffect(() => {
        if (isOpen) {
            setPrimaryTargets('');
            setSecondaryTargets('');
        }
    }, [isOpen]);

    // Set already saved data as valid initially.
    const [isChecking, setChecking] = useState(false);
    const [targetDictionary, setTargetDictionary] = useState({});
    const onCheck = async() => {
        setChecking(true);
        const Tp = T(primaryTargets), Ts = T(secondaryTargets);
        const data = await checkTargets(Tp.concat(Ts));
        const results = Object.fromEntries(data.map(x => [x.target, x.valid]));
        const _haveErrors = [
            Tp.some(t => !results[t]),
            Ts.some(t => !results[t])
        ];
        setHaveErrors(_haveErrors);
        setTargetDictionary(results);
        setChecking(false);
    };

    const [validated, setValidated] = useState(false);
    const [haveErrors, setHaveErrors] = useState([false, false]);
    useEffect(() => {
        if (Object.entries(targetDictionary).length === 0) return;
        const data = T(primaryTargets).concat(T(secondaryTargets));
        setValidated(data.every(t => !!targetDictionary[t]));
    }, [primaryTargets, secondaryTargets, targetDictionary]);

    const onImport = () => {
        onAdd({
            primary_targets: T(primaryTargets),
            secondary_targets: T(secondaryTargets)
        });
        setOpen(false);
    };

    return (
        <>
            <Button onClick={() => setOpen(true)}>
                Copy from Excel
            </Button>

            <Dialog
                open={isOpen}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth='sm'
            >

                <DialogTitle>
                    Copy from Excel
                </DialogTitle>

                <DialogContent>
                    <Grid container rowSpacing={2}>
                        <Grid item md={12}>
                            <DialogContentText>
                                Copy your targets from excel, paste them below then check them against the automarker.
                                Targets should be separated by a comma.
                            </DialogContentText>
                        </Grid>

                        <Grid item md={12}>
                            <Typography variant={'body'}>Primary targets to import</Typography>
                        </Grid>
                        <Grid item md={12}>
                            <TextField
                                multiline
                                minRows={2}
                                fullWidth
                                onChange={onPrimaryChange}
                            />
                        </Grid>
                        <Grid item md={12}>
                            <Typography variant={'body'}>Secondary targets to import</Typography>
                        </Grid>
                        <Grid item md={12}>
                            <TextField
                                multiline
                                minRows={2}
                                fullWidth
                                onChange={onSecondaryChange}
                            />
                        </Grid>

                        <Grid item md={12}></Grid>
                        <Button variant='contained' color='primary' disabled={validated} onClick={onCheck}>
                            Check Targets
                            { isChecking && <> &nbsp; <CircularProgress color="inherit" size={12} /> </>}
                        </Button>

                        <Grid item md={12   }><Divider variant={"fullWidth"}/></Grid>

                        {[
                            { hasErrors: haveErrors[0], name: 'Primary targets', list: primaryTargets },
                            { hasErrors: haveErrors[1], name: 'Secondary targets', list: secondaryTargets }
                         ].map(({ hasErrors, name, list }) =>
                                list.trim() !== '' && <Grid item md={12} key={name}>
                                    <Typography variant='body'>
                                        {`${name} that will be imported`}
                                    </Typography>

                                    {!!hasErrors &&
                                        <FormHelperText error>
                                            Some of the targets that were pasted in are not valid with the automarker.
                                            Please amend or delete them above before continuing.
                                        </FormHelperText>
                                    }

                                    <ChipList
                                        disabled
                                        readOnly
                                        disabledColor={null}
                                        targetDictionary={targetDictionary}
                                        value={T(list)}
                                        transformFunction={SpecialSymbolStrip}
                                    />
                                </Grid>

                        )}
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button variant='contained' color='primary' disabled={!validated} onClick={onImport}>
                        Import
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}